import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import { DistributionNotFound } from "../../components/distribution-not-found";
import GuardedRoute, { type GuardedRouteProps } from "../../components/guarded-route";
import { LimitReachedCommunicate } from "../../components/limit-reached-communicate";
import { FlowPathProvider } from "../../hooks/use-flow-path";
import { useResetDistributionState } from "../../hooks/use-reset-distribution-state";
import { getConfig } from "../../models/voucher-distribution";
import { useTermsAgreementState } from "../../store/use-terms-agreement-state";
import { useUserState } from "../../store/use-user-state";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import { childPath } from "../../utils/child-path";
import { isAuthorizationRequired } from "../../utils/is-authorization-required";
import { configName } from "../config/config-name";
import { routes } from "../config/routes";
import { Checkout } from "./checkout";
import { CodeValidation } from "./code-validation";
import { PickUpsell } from "./pick-upsell";
import { PickVoucher } from "./pick-voucher";
import { TermsAgreementPage } from "./terms-agreement";
import { ThankYouPage } from "./thank-you-page";
import { ValidateUser } from "./validate-user";

export const FreePackagesContent = () => {
  useResetDistributionState();
  const { path } = useRouteMatch();
  const { voucherDistribution } = useVouchersDistributionState();
  const { user } = useUserState();
  const acceptedInStore = useTermsAgreementState((s) => Boolean(s.acceptedConfigs[configName]));
  const config = getConfig(voucherDistribution, configName);

  if (!config) {
    return <DistributionNotFound />;
  }

  const rulesAcceptanceRequired = voucherDistribution?.rulesAcceptanceRequired ?? false;
  const isAccepted = acceptedInStore || !rulesAcceptanceRequired;
  const authorizationRequired = isAuthorizationRequired(config);

  const defaultGuardedRouteProps: GuardedRouteProps = {
    isAuthenticated: authorizationRequired ? !!user?.isValid : true,
    isAccepted,
    authenticationPath: childPath(path, routes.validateUser.raw()),
    agreementPath: childPath(path, routes.termsAgreement.raw()),
  };

  const postAuthPath = childPath(
    path,
    rulesAcceptanceRequired ? routes.termsAgreement.raw() : routes.pickVoucher.raw(),
  );

  return (
    <FlowPathProvider value={path}>
      <Switch>
        <Route path={childPath(path, routes.thankYou.raw())} component={ThankYouPage} exact />
        <Route>
          <LimitReachedLayout>
            <Switch>
              {authorizationRequired && (
                <Route path={childPath(path, routes.validateUser.raw())} component={ValidateUser} exact />
              )}
              {!authorizationRequired && (
                <Route
                  path={childPath(path, routes.validateUser.raw())}
                  exact
                  render={() => <Redirect to={postAuthPath} />}
                />
              )}
              <Route path={childPath(path, routes.codeValidation.raw())} component={CodeValidation} />
              <Route
                path={childPath(path, routes.termsAgreement.raw())}
                render={() => {
                  if (!rulesAcceptanceRequired) {
                    return <Redirect to={childPath(path, routes.pickVoucher.raw())} />;
                  }
                  return defaultGuardedRouteProps.isAuthenticated ? (
                    <TermsAgreementPage />
                  ) : (
                    <Redirect to={defaultGuardedRouteProps.authenticationPath} />
                  );
                }}
              />
              <GuardedRoute
                {...defaultGuardedRouteProps}
                path={childPath(path, routes.pickVoucher.raw())}
                component={PickVoucher}
              />
              <GuardedRoute
                {...defaultGuardedRouteProps}
                path={childPath(path, routes.pickUpsell.raw())}
                component={PickUpsell}
              />
              <GuardedRoute
                {...defaultGuardedRouteProps}
                path={childPath(path, routes.checkout.raw())}
                component={Checkout}
              />
              <Route component={DistributionNotFound} />
            </Switch>
          </LimitReachedLayout>
        </Route>
      </Switch>
    </FlowPathProvider>
  );
};

function LimitReachedLayout({ children }: { children: React.ReactNode }) {
  const { voucherDistribution } = useVouchersDistributionState();
  const config = getConfig(voucherDistribution, configName);

  if (config?.limitReached) {
    return <LimitReachedCommunicate className="mt-20" />;
  }

  return children;
}
